/**
 * ── LEGACY SHELL — SCHEDULED FOR DELETION ──────────────────────────────────
 *
 * The original AccountSelectionView, restored verbatim from `frontend_refactor`
 * so the legacy theme behaves exactly as the app did before the refactor: a
 * full-page "Select Dashboard" form reached from Portal Management, not the new
 * modal chooser.
 *
 * Three things changed. Two were forced: the import depth, and the closing
 * dispatch(updateMainView()) — that slice field no longer exists — which is now
 * an onDone() callback owned by PortalShell. The third is behavioural and
 * deliberate: the mount effect no longer bails when selectedClient is already
 * set, because the original's bail-out left a pre-seeded Manager with two
 * dropdowns that never populated (9e75912; Known-deviations list +
 * docs/traps/regressions.md §6).
 *
 * Deliberately NOT modernised. It keeps its own literal colours, its
 * five-errors-at-once validate(), and its layout, because the point is that it
 * is identical. Do not tidy it; delete it. See README.md in this folder.
 */
import { Autocomplete, Button, Grid, TextField, Typography } from "@mui/material";
import { SyntheticEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";

import {
  setSelectedClient,
  setSelectedProgram,
  setSelectedUserType,
  setSelectedSalesRep,
  setFilteredUserTypes,
  setSelectedSchoolDealer,
  portalManagementSearchReset,
  portalManagementReset
} from "../../../../store/features/portalManagement/portalManagementSlice";

import {
  getUserClients,
  getClientPrograms,
  getClientSalesRep,
  getTrainingUsers
} from "../../../../store/thunks/portalManagementThunk";

import { logoutUser } from "../../../../store/thunks/authenticationThunk";
import { systemManagementReset } from "../../../../store/features/systemManagement/systemManagementSlice";
import { logout } from "../../../../store/features/authentication/authenticationSlice";

import Spinner from "../../../reusableComponents/spinner/spinner";

import { RoleIndex } from "../../../../models/role";
import { UserType } from "../../../../models/userType";
import { Program } from "../../../../models/program";

type ErrorState = Record<"client" | "program" | "userType" | "salesRep" | "schoolDealer", { hasError: boolean; message: string }>;

const isClientUserRole = (u: any) => u?.role_index === RoleIndex.ClientUser;
const needsSalesRep = (t: any) => t?.client_user_type_index === UserType.Index.SalesRep;
const needsSchoolDealer = (t: any) => Program.AllowedUserTypesIndex[Program.Index.Training].includes(t?.client_user_type_index) &&
                                        t?.client_user_type_index !== UserType.Index.LWS_ADMIN &&
                                        t?.client_user_type_index !== UserType.Index.LWS_SUPPORT;

const LegacySelectionView = ({ onDone }: { onDone: () => void }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { user } = useAppSelector((s: any) => s.authentication);
  const {
    clients,
    programs,
    userTypes,
    filteredUserTypes,
    salesReps,
    trainingUsers,
    selectedClient,
    selectedProgram,
    selectedUserType,
    selectedSalesRep,
    selectedSchoolDealer,
    clientsLoading,
    programsLoading,
    salesRepsLoading,
    trainingUsersLoading
  } = useAppSelector(s => s.portalManagement);

  const isUserRole = isClientUserRole(user);
  const showClientField = !isUserRole;
  
  const base_error = {
    client: { hasError: false, message: "" },
    program: { hasError: false, message: "" },
    userType: { hasError: false, message: "" },
    salesRep: { hasError: false, message: "" },
    schoolDealer: { hasError: false, message: "" },
  }

  const [errors, setErrors] = useState<ErrorState>(base_error);
  // Only LWS_ADMIN gets the user-type filter; LWS_SUPPORT views all grade
  // items through its own scope and goes straight to the dashboard.
  const [isLWSAdmin, setIsLWSAdmin] = useState(false);

  const isTrainingProgram = selectedProgram?.program_index === Program.Index.Training;
  const showUserTypeField = !isTrainingProgram || isLWSAdmin;

  useEffect(() => {
    if (!isTrainingProgram) {
      setIsLWSAdmin(false);
      return;
    }
    if (!trainingUsers?.length) return;
    const match: any = (trainingUsers as any[]).find(
      (u: any) => u.clients_user_id === user?.client_user_index
    );
    if (match?.role_type === UserType.Name.LWS_ADMIN) setIsLWSAdmin(true);
  }, [isTrainingProgram, trainingUsers, user]);

  useEffect(() => {
    if (isTrainingProgram && !trainingUsers?.length && !trainingUsersLoading) {
      dispatch(getTrainingUsers({}));
    }
  }, [dispatch, isTrainingProgram, trainingUsers, trainingUsersLoading]);
  
  const setError = (field: keyof ErrorState, msg: string) =>
    setErrors(prev => ({ ...prev, [field]: { hasError: !!msg, message: msg } }));

  const clearErrors = () => setErrors(base_error);

  // Latest-ref: the effect's guards read current userTypes without re-firing
  // when getUserClients fills them.
  const userTypesRef = useRef(userTypes);
  userTypesRef.current = userTypes;

  useEffect(() => {
    // Mid-selection: leave it alone.
    if (selectedProgram || selectedUserType) return;
    // A client user may already have selectedClient seeded by dashboardView.
    // This used to bail on that, so nothing ever fetched their programs and the
    // chooser opened with both dropdowns empty. Having the client is a reason to
    // fetch its programs, not a reason to stop.
    // The programs-only shortcut assumes userTypes survived in the snapshot;
    // getUserClients is their only writer, so a snapshot with a client but no
    // types (captured before types persisted) must still fall through or the
    // User Type dropdown stays empty forever and validate() blocks.
    if (selectedClient && programs?.length && userTypesRef.current?.length) return;

    if (selectedClient?.client_index && userTypesRef.current?.length) {
      dispatch(getClientPrograms({ client_index: selectedClient.client_index }));
      return;
    }

    dispatch(getUserClients({}))
      .unwrap()
      .then(payload => {
        if (selectedClient?.client_index) {
          // Client already seeded; this run was for the user types.
          dispatch(getClientPrograms({ client_index: selectedClient.client_index }));
          return;
        }
        if (isUserRole && payload?.user_clients?.[0]) {
          const client = payload.user_clients[0];
          dispatch(setSelectedClient(client));
          dispatch(getClientPrograms({ client_index: client.client_index }));
        }
      })
      .catch(err => {
        if (err?.message === "Rejected") {
          dispatch(logoutUser({ user_index: user?.client_user_index }));
          dispatch(portalManagementReset());
          dispatch(systemManagementReset());
          dispatch(logout());
          navigate("/login");
        }
      });
  }, [dispatch, navigate, user, isUserRole, selectedClient, selectedProgram, selectedUserType, programs]);

  const handleChange = (setter: any, extra?: (value: any) => void) => (_: SyntheticEvent, value: any) => {
    clearErrors();
    dispatch(setter(value));
    extra?.(value);
  };

  const onClientChange = handleChange(setSelectedClient, (_selectedClient) => {
    if (!_selectedClient?.client_index) return;
    dispatch(portalManagementSearchReset());
    dispatch(getClientPrograms({ client_index: _selectedClient.client_index }));
  });

  const onProgramChange = handleChange(setSelectedProgram, (_selectedProgram) => {
    if (!_selectedProgram?.program_index) {
      dispatch(setFilteredUserTypes([]));
      return;
    }

    const programUserTypes = userTypes.filter((user_type: any) => user_type.program_index === _selectedProgram.program_index )
    dispatch(setFilteredUserTypes(programUserTypes));
  });

  const onUserTypeChange = handleChange(setSelectedUserType, (_selectedUserType) => {
    if (needsSalesRep(_selectedUserType)) {
      dispatch(getClientSalesRep({ customer_id: selectedClient?.customer_id }));
    }
  });

  const onSalesRepChange = handleChange(setSelectedSalesRep);
  const onSchoolDealerChange = handleChange(setSelectedSchoolDealer);

  const validate = () => {
    clearErrors();
    let valid = true;

    if (showClientField && !selectedClient)    { setError("client",    "Client required"); valid = false; }
    if (!selectedProgram)                      { setError("program",   "Program required"); valid = false; }
    if (showUserTypeField && !selectedUserType) { setError("userType",  "Type required"); valid = false; }
    if (showUserTypeField && needsSalesRep(selectedUserType) && !selectedSalesRep) {
      setError("salesRep", "Sales rep required"); valid = false;
    }
    if (showUserTypeField && needsSchoolDealer(selectedUserType) && !selectedSchoolDealer) {
      setError("schoolDealer", "School or dealer required"); valid = false;
    }

    if (valid) {
      dispatch(portalManagementSearchReset());
      // Was dispatch(updateMainView()); mainView no longer exists, so the
      // owner of this view decides what leaving it means.
      onDone();
    }
  };

  return (
    <Grid
        direction="column" alignItems="center" justifyContent="center"
        sx={{ border: "1px solid #ddd",
        bgcolor: "white",
        borderRadius: 4,
        m: 3,
        p: 10
      }}>

      <Grid container justifyContent="center" sx={{ mb: 4 }}>
        <Spinner loading={clientsLoading || programsLoading || salesRepsLoading || trainingUsersLoading} />
        <Typography variant="h5" sx={{ color: "#3B934A", fontWeight: "bold", mb: 3 }}>
        Select Dashboard
        </Typography>
      </Grid>

      <Grid container spacing={4}>
        {showClientField && (
          <Field label="Client(s)">
            <Autocomplete
              value={selectedClient}
              onChange={onClientChange}
              options={clients ?? []}
              getOptionLabel={(o: any) => o?.customer_name ?? ""}
              renderInput={p => <TextField {...p} error={errors.client.hasError} helperText={errors.client.message} placeholder="Select client" />}
            />
          </Field>
        )}

        <Field label="Program(s)">
          <Autocomplete
            value={selectedProgram}
            onChange={onProgramChange}
            options={programs ?? []}
            getOptionLabel={(o: any) => Program.DisplayName[o.program_index as keyof typeof Program.DisplayName ] ?? o.program_name ?? ""}
            renderInput={p => <TextField {...p} error={errors.program.hasError} helperText={errors.program.message} placeholder="Select Program" />}
          />
        </Field>

        {showUserTypeField && (
          <Field label="User Type(s)">
            <Autocomplete
              value={selectedUserType}
              onChange={onUserTypeChange}
              options={filteredUserTypes ?? []}
              getOptionLabel={(o: any) => o.label ?? ""}
              renderInput={p => <TextField {...p} error={errors.userType.hasError} helperText={errors.userType.message} placeholder="Select User Type" />}
            />
          </Field>
        )}

        {showUserTypeField && needsSchoolDealer(selectedUserType) && (
          <Field label="School/Dealer(s)">
            <Autocomplete
              value={selectedSchoolDealer}
              onChange={onSchoolDealerChange}
              options={trainingUsers?.filter((u: any) => u.role_type === selectedUserType?.name) ?? []}
              getOptionLabel={(o: any) => (o.r_operand) + " (" + (o?.first_name) + " " + (o?.last_name) + ")" }
              renderInput={p => <TextField {...p} error={errors.schoolDealer.hasError} helperText={errors.schoolDealer.message} placeholder="Select School or Dealer" />}
            />
          </Field>
        )}

        {showUserTypeField && needsSalesRep(selectedUserType) && (
          <Field label="Sales Rep(s)">
            <Autocomplete
              value={selectedSalesRep}
              onChange={onSalesRepChange}
              options={salesReps ?? []}
              getOptionLabel={(o: any) => o?.SALESMAN ?? o?.name ?? ""}
              renderInput={p => <TextField {...p} error={errors.salesRep.hasError} helperText={errors.salesRep.message} placeholder="Select Sales Rep" />}
            />
          </Field>
        )}
      </Grid>

      <Grid container justifyContent="center" sx={{ mt: 5 }}>
        <Button variant="contained" sx={{ minWidth: 220, py: 1.5, bgcolor: "#004680" }} onClick={validate}>
          View Dashboard
        </Button>
      </Grid>
    </Grid>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Grid item xs={12} md={6}>
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={12} sm={4} sx={{ textAlign: { sm: "right" } }} justifyContent="">
          <Typography variant="h6" fontWeight={500}>{label}:</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>{children}</Grid>
      </Grid>
    </Grid>
  );
}

export default LegacySelectionView;